import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Plus, Pencil, Trash2, Music, ExternalLink, GripVertical } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDjs() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const api = useCallback((method, url, data) => {
    return axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}` } });
  }, [token]);

  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', logo_url: '', description: '', website_url: '', phone: '', specialties: '', order: 0 });

  useEffect(() => {
    if (!token) navigate('/admin');
  }, [token, navigate]);

  const loadDjs = useCallback(async () => {
    try {
      const res = await api('get', '/api/admin/djs');
      setDjs(res.data || []);
    } catch { /* */ }
    setLoading(false);
  }, [api]);

  useEffect(() => { loadDjs(); }, [loadDjs]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', logo_url: '', description: '', website_url: '', phone: '', specialties: '', order: djs.length });
    setDialogOpen(true);
  };

  const openEdit = (dj) => {
    setEditing(dj);
    setForm({ name: dj.name, logo_url: dj.logo_url || '', description: dj.description || '', website_url: dj.website_url || '', phone: dj.phone || '', specialties: dj.specialties || '', order: dj.order || 0 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api('put', `/api/admin/djs/${editing.id}`, form);
      } else {
        await api('post', '/api/admin/djs', form);
      }
      setDialogOpen(false);
      loadDjs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce DJ ?')) return;
    try {
      await api('delete', `/api/admin/djs/${id}`);
      loadDjs();
    } catch { /* */ }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="admin-djs-back">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl flex items-center gap-2">
              <Music className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
              DJ partenaires
            </h1>
          </div>
          <Button data-testid="add-dj-btn" onClick={openCreate} size="sm" className="bg-[#C4A962] hover:bg-[#b5945a] text-white rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-[#8A9A5B] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : djs.length === 0 ? (
          <div data-testid="admin-djs-empty" className="text-center py-16 bg-white rounded-2xl border border-[#E5E5E0]">
            <Music className="w-12 h-12 text-[#C4A962]/40 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#595959] mb-4">Aucun DJ partenaire pour le moment.</p>
            <Button data-testid="add-first-dj-btn" onClick={openCreate} className="bg-[#8A9A5B] hover:bg-[#7a8a4d] text-white rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Ajouter un DJ
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#E5E5E0] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0]">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>DJ / Animateur</TableHead>
                  <TableHead className="hidden md:table-cell">Specialites</TableHead>
                  <TableHead className="hidden md:table-cell">Contact</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {djs.map(dj => (
                  <TableRow key={dj.id} data-testid={`admin-dj-row-${dj.id}`}>
                    <TableCell>
                      <GripVertical className="w-4 h-4 text-[#C4A962]/40" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {dj.logo_url ? (
                          <img src={dj.logo_url} alt={dj.name} className="w-10 h-10 rounded-lg object-cover border border-[#E5E5E0]" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-[#F5F5F0] flex items-center justify-center">
                            <Music className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#2C3E30] text-sm">{dj.name}</p>
                          {dj.description && <p className="text-xs text-[#595959] line-clamp-1">{dj.description}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm text-[#595959]">{dj.specialties || '-'}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="text-sm text-[#595959] space-y-0.5">
                        {dj.phone && <p>{dj.phone}</p>}
                        {dj.website_url && (
                          <a href={dj.website_url} target="_blank" rel="noopener noreferrer" className="text-[#C4A962] hover:underline inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Site
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" data-testid={`edit-dj-${dj.id}`} onClick={() => openEdit(dj)}>
                          <Pencil className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-dj-${dj.id}`} onClick={() => handleDelete(dj.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="dj-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-[#2C3E30]">
              {editing ? 'Modifier le DJ' : 'Ajouter un DJ'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Nom *</label>
              <Input data-testid="dj-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nom du DJ / animateur" className="rounded-lg" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Specialites</label>
              <Input data-testid="dj-specialties-input" value={form.specialties} onChange={e => setForm(f => ({ ...f, specialties: e.target.value }))} placeholder="Ex: Mariages, Soirees dansantes, Karaoke..." className="rounded-lg" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Description</label>
              <Textarea data-testid="dj-desc-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description du DJ..." className="rounded-lg min-h-[80px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Telephone</label>
                <Input data-testid="dj-phone-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="06 12 34 56 78" className="rounded-lg" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Site web</label>
                <Input data-testid="dj-website-input" value={form.website_url} onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))} placeholder="https://..." className="rounded-lg" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">URL du logo</label>
              <Input data-testid="dj-logo-input" value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="https://... (optionnel)" className="rounded-lg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
            <Button data-testid="dj-save-btn" onClick={handleSave} disabled={!form.name} className="rounded-full bg-[#8A9A5B] hover:bg-[#7a8a4d] text-white">
              {editing ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
